import { useCallback } from 'react';
import { flushWebLogger, webLog } from '../../../shared/api/grpc/webLogger';
import { useQuickFillStore } from '../../../shared/stores/useQuickFillStore';
import {
  getAutoFillPreview,
  type QuickFillAutoFillMode,
} from '../../../shared/lib/utils/autoFillService';
import {
  AUTO_FILL_LOG_SAMPLE_SIZE,
  AUTO_FILL_LOG_SOURCE,
  stringifyAutoFillLog,
  summarizeAutoFillRow,
} from '../lib/quickFillTableOperationHelpers';
import type { QuickFillMutationOperations } from './useQuickFillMutationOperations';

export type QuickFillAutoFillContext = Pick<QuickFillMutationOperations,
  | 'quickFillData'
  | 'properties'
  | 'selectedRows'
  | 'snackbar'
  | 'captureSessionUndoBeforeMutation'
  | 'releaseEmptySessionUndoCapture'
  | 'autoFillRowsWithLabels'
>;

export function useQuickFillAutoFillOperations(context: QuickFillAutoFillContext) {
  const {
    quickFillData, properties, selectedRows, snackbar, captureSessionUndoBeforeMutation,
    releaseEmptySessionUndoCapture, autoFillRowsWithLabels,
  } = context;

  const handleAutoFillPreview = useCallback((mode: 'all' | 'selected') => {
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    const checkedLabels = (properties || []).filter(
      (property) => property?.checked
    ).map((property) => property.label);
    const candidateRows = mode === 'selected'
      ? selectedRows
      : data.map((_item, index) => index);
    if (mode === 'selected' && selectedRows.length === 0) {
      webLog.warn(AUTO_FILL_LOG_SOURCE, stringifyAutoFillLog('preview-no-selection', {
        mode,
        totalRows: data.length,
        checkedLabels,
      }));
      void flushWebLogger();
      snackbar.showWarning('Please select rows to auto-fill');
      return null;
    }
    const sourceRows = candidateRows.map(
      (rowIndex) => summarizeAutoFillRow(data[rowIndex], rowIndex)
    );
    const familyNames = sourceRows.map((row) => row.source).filter(Boolean);
    const preview = getAutoFillPreview(familyNames);
    const elapsedMs = Math.round(
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt
    );
    const details = {
      mode,
      elapsedMs,
      totalRows: data.length,
      candidateRows: candidateRows.length,
      sourceNames: familyNames.length,
      uniqueSourceNames: new Set(familyNames).size,
      emptySourceRows: sourceRows.filter((row) => !row.source).length,
      checkedLabels,
      sampleRows: sourceRows.slice(0, AUTO_FILL_LOG_SAMPLE_SIZE),
    };
    if (preview.matches === 0) {
      webLog.warn(AUTO_FILL_LOG_SOURCE, stringifyAutoFillLog('preview-zero-matches', details));
      void flushWebLogger();
      snackbar.showWarning(
        'No matches found for the family names. Auto-fill patterns may not recognize these naming conventions.'
      );
      return null;
    }
    webLog.info(AUTO_FILL_LOG_SOURCE, stringifyAutoFillLog('preview-ready', {
      ...details,
      matches: preview.matches,
    }));
    return preview;
  }, [properties, quickFillData, selectedRows, snackbar]);

  const handleConfirmAutoFill = useCallback((mode: 'all' | 'selected') => {
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    try {
      const undoCapture = captureSessionUndoBeforeMutation('Auto-fill');
      const checkedLabels = (properties || []).filter(
        (property) => property?.checked
      ).map((property) => property.label);
      const rowIndices = mode === 'selected'
        ? selectedRows
        : quickFillData.map((_item, index) => index);
      const result = autoFillRowsWithLabels(rowIndices, checkedLabels, 'overwrite');
      const pendingState = useQuickFillStore.getState();
      webLog.warn(AUTO_FILL_LOG_SOURCE, stringifyAutoFillLog('hook-apply-pending-state', {
        mode,
        selectedRows: selectedRows.length,
        requestedRows: rowIndices.length,
        filled: result.filled,
        total: result.total,
        checkedLabels,
        editedCellCount: Object.values(pendingState.editedCells || {}).filter(Boolean).length,
        hasPendingChanges: pendingState.hasPendingChanges,
        baselineRows: pendingState.baselineQuickFillData.length,
        currentRows: pendingState.quickFillData.length,
        sampleEditedKeys: Object.keys(pendingState.editedCells || {}).slice(0, AUTO_FILL_LOG_SAMPLE_SIZE),
      }));
      void flushWebLogger();
      const matchedRows = Array.from(result.results.values()).filter(
        (item) => item.confidence > 0
      ).length;
      if (result.filled === 0) {
        releaseEmptySessionUndoCapture(undoCapture);
        const payload = {
          mode,
          applyMode: 'overwrite',
          elapsedMs: Math.round(
            (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt
          ),
          selectedRows: selectedRows.length,
          matchedRows,
          total: result.total,
          resultSamples: Array.from(result.results.entries())
            .slice(0, AUTO_FILL_LOG_SAMPLE_SIZE)
            .map(([rowIndex, item]) => ({
              ...summarizeAutoFillRow(quickFillData[rowIndex], rowIndex),
              ...item,
            })),
        };
        webLog[matchedRows > 0 ? 'info' : 'warn'](
          AUTO_FILL_LOG_SOURCE,
          stringifyAutoFillLog(matchedRows > 0 ? 'apply-no-changes' : 'apply-zero-filled', payload)
        );
        void flushWebLogger();
      } else {
        webLog.info(AUTO_FILL_LOG_SOURCE, stringifyAutoFillLog('apply-complete', {
          mode,
          applyMode: 'overwrite',
          elapsedMs: Math.round(
            (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt
          ),
          selectedRows: selectedRows.length,
          filled: result.filled,
          total: result.total,
        }));
      }
      if (result.filled > 0) {
        snackbar.showSuccess(
          `Auto-filled ${result.filled} of ${result.total} rows based on family name patterns`
        );
      } else if (matchedRows > 0) {
        snackbar.showInfo(
          'No Quick Fill cells changed; matched values were already present or unavailable for the checked columns.'
        );
      } else {
        snackbar.showWarning(
          'No Quick Fill cells changed. Auto-fill patterns may not recognize the current rows.'
        );
      }
    } catch (error) {
      webLog.error(AUTO_FILL_LOG_SOURCE, stringifyAutoFillLog('apply-error', {
        mode,
        message: error instanceof Error ? error.message : String(error),
      }));
      void flushWebLogger();
      snackbar.showError('Failed to auto-fill. Please try again.');
    }
  }, [
    autoFillRowsWithLabels, captureSessionUndoBeforeMutation, properties,
    quickFillData, releaseEmptySessionUndoCapture, selectedRows, snackbar,
  ]);

  const applyAutoFillToRows = useCallback((
    rowIndices: number[], labels: string[], mode: QuickFillAutoFillMode = 'fillEmpty'
  ) => {
    const undoCapture = captureSessionUndoBeforeMutation('Auto-fill');
    const result = autoFillRowsWithLabels(rowIndices, labels, mode);
    if (result.filled === 0) releaseEmptySessionUndoCapture(undoCapture);
    return result;
  }, [autoFillRowsWithLabels, captureSessionUndoBeforeMutation, releaseEmptySessionUndoCapture]);

  return {
    handleAutoFillPreview,
    handleConfirmAutoFill,
    applyAutoFillToRows,
  };
}
